import React, { useState, useEffect, useContext, useReducer } from "react";
import { Form, Tree, Switch, Tooltip, Menu } from "antd";
import {
  InputField,
  PrimaryButton,
  Card,
  Icons,
  Checkbox,
  Layout,
  Background,
  Typography,
  TextLink,
  PasswordField,
  Tag,
  InvertedPrimaryButton,
  Loader,
} from "../../components";
import {
  EMPTY_REQUIRED_FIELD_MESSAGE,
  INVALID_EMAIL_MESSAGE,
  IP_REGEX,
  LOCALHOST_REGEX,
  WEBHOOK_NAME_EXCEED_LIMIT_MESSAGE,
  URL_REGEX,
} from "../../constants/constants";
// import availableWebhooks from "../../constants/available-webhooks.json";
import { getAllWebhookEventsApi } from "../../api";
import { useNavigate } from "react-router-dom";
import "./UpsertWebhookForm.scss";
import useFormSubmitDisable from "../../hooks/useFormSubmitDisable.js";
import { DOMAIN_NOT_HTTPS, NOT_A_VALID_DOMAIN } from "../../constants/constants";
import { UserContext } from "../../storage/context";

const requiredFormFields = ["name", "url"];

const webhookUrlRules = [
  {
    validator: (_, value) => {
      if (value === "") {
        return Promise.reject(EMPTY_REQUIRED_FIELD_MESSAGE);
      }
      if (!value.startsWith("https://")) {
        return Promise.reject(DOMAIN_NOT_HTTPS);
      }
      if (LOCALHOST_REGEX.test(value) || IP_REGEX.test(value) || !URL_REGEX.test(value) || value.length <= 8) {
        return Promise.reject(NOT_A_VALID_DOMAIN);
      }

      Promise.resolve();
    },
  },
];

const treeNodeStyle = {
  border: "1px solid #F7F7FB",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
};

const formatDataToRequiredFormat = (data) => {
  const result = {};
  const selectedItems = [];
  data.forEach((item) => {
    const category = item.name.split(".")[0];
    // const titleElement =
    if (!result[category]) {
      result[category] = {
        title: category,
        key: category,
        children: [],
        isLeaf: false,
        style: treeNodeStyle,
      };
    }
    const { id, name, display_name, description } = item;
    result[category].children.push({
      title: name,
      key: name,
      description,
      isLeaf: true,
      style: treeNodeStyle,
    });

    selectedItems.push(name);
  });

  // let selectedItems = [];
  // Object.values(result).forEach((item) => {
  //   selectedItems = [...selectedItems, ...item.children];
  // });

  const root = [
    {
      title: "Select all events",
      key: "root",
      children: Object.values(result),
      backgroundColor: "#EBEBEF",
      isLeaf: false,
      style: {
        ...treeNodeStyle,
        backgroundColor: "#EBEBEF",
        borderRadius: "4px 4px 0px 0px",
      },
    },
    {
      title: "Total selected events",
      key: "custom-div",
      // children: Object.values(result),
      backgroundColor: "#EBEBEF",
      checkable: false,
      style: {
        ...treeNodeStyle,
        backgroundColor: "#EBEBEF",
        borderRadius: "0px 0px 4px 4px",
        color: "#8A8894",
        fontWeight: "500",
      },
    },
  ];

  return { root, selectedItems };
};

export default function UpsertWebhookForm({
  title = "Create webhook",
  isUpdate = false,
  onSubmitForm,
  webhookName = "",
  webhookURL = "",
  isActive = false,
  onCancel,
  errors = { errorMessage: "" },
  selectedWebhookEvents = [],
  buttonLoading = false,
}) {
  const navigate = useNavigate();
  const [formInstance] = Form.useForm();
  const { userProfile } = useContext(UserContext);
  const environment = userProfile.environment.toUpperCase();
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [urlRequirements, setUrlRequirements] = useState({
    isHTTPS: false,
    isNotIP: false,
    isNotLocalDomain: false,
  });

  const disableFormSubmit = useFormSubmitDisable(formInstance, requiredFormFields);

  const onClickSubmitForm = () => {
    onSubmitForm(formInstance.getFieldValue());
  };

  useEffect(() => {
    const fetchWebhookEvents = async () => {
      try {
        const { data } = await getAllWebhookEventsApi.getAllWebhookEvents(environment);
        const { root, selectedItems } = formatDataToRequiredFormat(data);
        setWebhookEvents(root);
        if (selectedWebhookEvents.length > 0) {
          formInstance.setFieldsValue({ events: selectedWebhookEvents });
        } else {
          formInstance.setFieldsValue({ events: selectedItems });
        }
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }

      // TODO: insert after the api call
      // setWebhookEvents(formatDataToRequiredFormat(.data));
    };
    setLoading(true);
    fetchWebhookEvents();
  }, []);

  // By default user changing values in form doesn't rerender this component
  // but for update webhook page we needed to show active/disabled state of webhook
  // which is dynamically changed
  // so It was not updating the component, hence we added this state
  // the sole purpose is to update the state and rerender the component
  const [_formFields, updateFormFields] = useState(false); // pls dont remove before reading above
  const [hasTouchedUrl, setHasTouchedUrl] = useState(false);

  const validateURL = (url) => {
    if (url.length > 0 && !hasTouchedUrl) {
      setHasTouchedUrl(true);
    }

    const urlRequirements = {
      isHTTPS: url.startsWith("https://"),
      isNotIP: !IP_REGEX.test(url),
      isNotLocalDomain: !LOCALHOST_REGEX.test(url),
    };
    setUrlRequirements(urlRequirements);
  };

  const onCheck = (_selectedKeys, event) => {
    const events = [];
    event.checkedNodes.forEach((item) => {
      if (item.isLeaf) {
        events.push(item.title);
      }
    });
    formInstance.setFieldsValue({ events });
  };

  const calculateCheckedWebhooks = (title) => {
    // if (!formInstance.getFieldValue("events")) {
    //   formInstance.setFieldsValue("envent", []);
    // }
    const values = formInstance.getFieldValue("events") || [];
    // if (!formInstance.getFieldValue("events")?.length > 0) return 0;

    return values.filter((item) => {
      if (item.startsWith(title + ".")) {
        return true;
      }
    }).length;
  };

  const setToastColor = (value) => {
    if (!hasTouchedUrl && !isUpdate) {
      return "";
    }
    if (!hasTouchedUrl && isUpdate) {
      return "success";
    }
    return value ? "success" : "error";
  };

  useEffect(() => {
    formInstance.setFieldsValue({
      is_active: isActive,
      name: webhookName,
      url: webhookURL,
    });

    updateFormFields();
  }, [isActive, webhookURL, webhookName]);
  // useEffect(() => {}, [formInstance]);

  if (!webhookEvents.length > 0 || loading) {
    return <Loader />;
  }
  return (
    <div>
      <Form
        className="webhook-upsert-form"
        onFinish={onSubmitForm}
        form={formInstance}
        // initialValues={{ events: [] }}
      >
        <div className="upsert-title-with-icon">
          <Icons.back_arrow onClick={() => navigate("/developers/webhooks")} className="icon-button" />
          <Typography h1 className="upsert-title">
            {title}
          </Typography>
        </div>
        {/* <p className="error-message-text">{errors.errorMessage} </p> */}

        <Form.Item
          name="name"
          rules={[
            { required: true, message: EMPTY_REQUIRED_FIELD_MESSAGE },
            { max: 100, message: WEBHOOK_NAME_EXCEED_LIMIT_MESSAGE },
          ]}
        >
          <InputField inputLabel="name" name="name" placeholder="Name to identify your webhook" defaultValue={webhookName} maxlength={101} />
        </Form.Item>
        <Form.Item name="url" rules={webhookUrlRules}>
          <InputField
            inputLabel="URL"
            placeholder="URL for receiving webhook events (e.g. https://avengers.com/webhook)"
            name="url"
            onChange={async (e) => {
              const url = "https://" + e.target.value;
              validateURL(url);
              formInstance.setFieldsValue({ url });
              await formInstance.validateFields(["url"]);
            }}
            defaultValue={webhookURL.replace("https://", "")}
            addonBefore="https://"
          />
          <div className="input-suggesting-tags">
            {/* <Tag color={setToastColor(urlRequirements.isHTTPS || webhookURL)}>
              https
            </Tag> */}
            <Tag color={setToastColor(urlRequirements.isNotIP)}>no IP addresses</Tag>
            <Tag color={setToastColor(urlRequirements.isNotLocalDomain)}>no localhost</Tag>
          </div>
        </Form.Item>
        {isUpdate && (
          <Form.Item name="is_active">
            <div className="webhook-state-update-container">
              <Switch
                defaultChecked={isActive}
                onChange={async (is_active) => {
                  await formInstance.setFieldsValue({ is_active });
                  updateFormFields(is_active);
                }}
              />
              <div className="webhook-state-banner">
                {formInstance.getFieldValue("is_active") === true ? (
                  <span className="active-state">Enabled</span>
                ) : (
                  <span className="inactive-state">Disabled</span>
                )}
              </div>
            </div>
          </Form.Item>
        )}
        <Form.Item shouldUpdate>
          {() => (
            <div>
              <label htmlFor="events-tree" className="input-label" style={{ marginBottom: "8px" }}>
                Events
              </label>
              <Tree
                id="events-tree"
                label="Events"
                checkable
                treeData={webhookEvents || []}
                defaultCheckedKeys={selectedWebhookEvents.length > 0 ? selectedWebhookEvents : ["root"]}
                style={{ padding: "2px" }}
                checkedKeys={formInstance.getFieldValue("events")}
                // switcherIcon={false}
                selectable={false}
                // rootStyle={{ backgroundColor: "#EBEBEF" }}
                onCheck={onCheck}
                // blockNode
                // showIcon
                // showLine
                defaultExpandedKeys={["root", "ACCOUNTS"]}
                titleRender={(item) => {
                  if (item.key === "custom-div") {
                    return (
                      <div className="custom-div-container webhook-section-card">
                        {[undefined].map(() => {
                          const totalSelectedItems = formInstance.getFieldValue("events").length;
                          return totalSelectedItems + (totalSelectedItems === 1 ? " event " : " events ") + " selected";
                        })}
                        <span
                          onClick={() => {
                            formInstance.setFieldsValue({ events: [] });
                          }}
                        >
                          <u>Clear all events</u>
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div className={item.isLeaf ? "webhook-section-card" : "webhook-section-card root-card-section-card"}>
                      <div className="webhook-event-title">
                        <Typography h4 style={item.isLeaf ? { color: "#757480" } : {}}>
                          {item.title}
                        </Typography>
                        {item?.description && (
                          <Tooltip title={item.description}>
                            <Icons.info_icon />
                          </Tooltip>
                        )}
                      </div>
                      {!item.isLeaf && item.key !== "root" && (
                        <div className="side-options">
                          <p>
                            {[null].map(() => {
                              const selectedItems = calculateCheckedWebhooks(item.title);
                              return selectedItems + (selectedItems === 1 ? " event " : " events ");
                            })}
                            selected
                          </p>
                          {/* <Icons.drop_down_fill /> */}
                        </div>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          )}
        </Form.Item>
        <p className="error-message-text">{errors.errorMessage} </p>
        <div className="webhook-form-buttons">
          <InvertedPrimaryButton label="Cancel" onClick={onCancel} />
          <Form.Item shouldUpdate noStyle>
            {() => (
              <PrimaryButton
                loading={buttonLoading}
                label={isUpdate ? "Save changes" : "Create webhook"}
                htmlType="submit"
                disabled={!formInstance.getFieldValue("events").length > 0 || (isUpdate ? disableFormSubmit(true) : disableFormSubmit())}
                onClick={onClickSubmitForm}
                type="submit"
              />
            )}
          </Form.Item>
        </div>
      </Form>
    </div>
  );
}
